import * as Sentry from '@sentry/vue';

interface CustomErrorOptions {
	description?: string;
	cause?: unknown;
	context?: Record<string, unknown>;
}

const UUID_PATTERN =
	/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const normalize = (text: string): string => text.replace(UUID_PATTERN, ':id');

export class CustomError extends Error {
	public readonly description: string = '';
	private readonly options?: CustomErrorOptions;

	get message(): string {
		const description = this.options?.description ?? this.description;
		const cause = this.cause;

		const causeText = normalize(
			cause instanceof Error ? cause.message : String(cause ?? '')
		);
		const causeMessage = causeText ? `(caused by: ${causeText})` : '';

		return `${description} ${causeMessage}`.trim();
	}

	protected constructor(options?: CustomErrorOptions) {
		super();

		this.cause = options?.cause;
		this.options = options;
	}

	static create(options?: CustomErrorOptions): CustomError {
		const customError = new this(options);

		return customError;
	}

	static report(options?: CustomErrorOptions): CustomError {
		const customError = new this(options);
		customError.report();

		return customError;
	}

	report(): void {
		try {
			if (this.options?.context) {
				Sentry.captureException(this, {
					contexts: {
						customError: this.options.context,
					},
				});

				return;
			}

			Sentry.captureException(this);
		} catch (error) {
			console.error('Error reporting error to sentry:', error);
		}
	}

	debug(): void {
		console.error('CustomError debug', this);
	}
}
